import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Gift, ExternalLink, MessageCircle, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme configurations
const themes = {
  navy: {
    bg: 'bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900',
    text: 'text-white',
    accent: 'text-cyan-400',
    button: 'bg-cyan-500 hover:bg-cyan-400 text-slate-900',
    secondaryBtn: 'bg-white/10 hover:bg-white/20 text-white border border-white/20',
    close: 'text-white/60 hover:text-white hover:bg-white/10',
  },
  aqua: {
    bg: 'bg-gradient-to-r from-cyan-600 via-teal-500 to-cyan-600',
    text: 'text-white',
    accent: 'text-yellow-300',
    button: 'bg-white hover:bg-slate-100 text-cyan-700',
    secondaryBtn: 'bg-white/20 hover:bg-white/30 text-white border border-white/30',
    close: 'text-white/60 hover:text-white hover:bg-white/10',
  },
  default: {
    bg: 'bg-gradient-to-r from-primary via-primary/90 to-primary',
    text: 'text-white',
    accent: 'text-yellow-300',
    button: 'bg-white hover:bg-slate-100 text-primary',
    secondaryBtn: 'bg-white/10 hover:bg-white/20 text-white border border-white/20',
    close: 'text-white/60 hover:text-white hover:bg-white/10',
  },
};

// Track promo events
const trackPromoEvent = async (eventType, config) => {
  if (!config?.trackingEnabled) return;
  
  try {
    await fetch(`${API_URL}/api/public/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: window.location.pathname,
        cta_key: `promo_${config.campaignId}_${eventType}`,
        label: `Promo ${eventType}: ${config.title}`,
        href: eventType === 'primary_click' ? config.primaryCtaLink : config.secondaryCtaLink || 'dismissed',
      }),
    });
  } catch (e) {
    console.error('Failed to track promo event:', e);
  }
};

// Check if within date window
const isWithinDateWindow = (startAt, endAt) => {
  const now = new Date();
  if (startAt && new Date(startAt) > now) return false;
  if (endAt && new Date(endAt) < now) return false;
  return true;
};

// Check dismiss frequency
const isDismissedRecently = (campaignId, frequencyDays, frequencyMode) => {
  try {
    const key = `promo_dismiss_${campaignId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return false;
    
    const { timestamp, mode } = JSON.parse(stored);
    const daysSince = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
    
    // Only apply frequency check if modes match
    if (frequencyMode === mode || frequencyMode === 'dismiss') {
      return daysSince < frequencyDays;
    }
    return false;
  } catch {
    return false;
  }
};

// Store dismiss
const storeDismiss = (campaignId, mode) => {
  try {
    const key = `promo_dismiss_${campaignId}`;
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), mode }));
  } catch {
    // Ignore storage errors
  }
};

// Get device type
const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth < 1024 ? 'mobile' : 'desktop';
};

// Image Lightbox Component for zoom/pan
const ImageLightbox = ({ src, alt, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.5, 1));
    if (scale <= 1.5) setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (scale > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && scale > 1 && e.touches.length === 1) {
      setPosition({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Controls */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button
          onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          aria-label="Zoom out"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          aria-label="Zoom in"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <button
          onClick={onClose}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Zoom indicator */}
      {scale > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white/70 text-sm">
          <Move className="w-4 h-4" />
          Drag to pan • {Math.round(scale * 100)}%
        </div>
      )}

      {/* Image */}
      <div
        className="max-w-[95vw] max-h-[90vh] overflow-hidden cursor-move"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-[90vh] object-contain transition-transform duration-200"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            cursor: scale > 1 ? 'grab' : 'zoom-in',
          }}
          draggable={false}
        />
      </div>
    </div>
  );
};

// Poster Popup Component - Image-first, responsive popup
const PosterPopup = ({ config, theme, onDismiss, globalPresets }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  
  // Delay visibility
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), config.delayMs || 2000);
    return () => clearTimeout(timer);
  }, [config.delayMs]);
  
  // Track impression
  useEffect(() => {
    if (isVisible) {
      trackPromoEvent('popup_impression', config);
    }
  }, [isVisible, config]);
  
  const handlePrimaryClick = () => {
    trackPromoEvent('popup_primary_click', config);
    if (config.frequencyMode === 'click') {
      storeDismiss(config.campaignId, 'click');
    }
  };
  
  const handleDismiss = () => {
    trackPromoEvent('popup_dismiss', config);
    // Only store in localStorage if frequencyMode is 'dismiss' 
    // But for popup_topbar mode, we want the topbar to persist
    // So we just use session-level dismissal for the popup
    onDismiss();
  };

  const handleImageClick = () => {
    setShowLightbox(true);
    trackPromoEvent('popup_image_enlarge', config);
  };
  
  const imageUrl = config.imageUrl?.startsWith('/') ? `${API_URL}${config.imageUrl}` : config.imageUrl;
  const isExternalLink = (link) => link?.startsWith('http') || link?.startsWith('tel:') || link?.startsWith('mailto:');
  
  // Reduced motion check
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  
  if (!isVisible) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
        aria-hidden="true"
      />
      
      {/* Popup */}
      <div 
        className={`fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 
          z-50 bg-slate-900 rounded-2xl shadow-2xl overflow-hidden
          max-w-lg w-full sm:max-h-[90vh] flex flex-col
          ${prefersReducedMotion ? '' : 'animate-in zoom-in-95 fade-in duration-300'}`}
        role="dialog"
        aria-labelledby="poster-popup-title"
        aria-modal="true"
        data-testid="promo-poster-popup"
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 p-2 bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors"
          aria-label="Close promotion"
          data-testid="poster-popup-close"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          {/* Image - Dominant Visual */}
          {imageUrl && (
            <div 
              className="relative cursor-pointer group"
              onClick={handleImageClick}
            >
              <img 
                src={imageUrl}
                alt={config.title || 'Holiday Program Flyer'}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '60vh' }}
                loading="eager"
              />
              {/* Tap to enlarge hint */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <span className="bg-black/60 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2">
                  <ZoomIn className="w-4 h-4" />
                  Tap to enlarge
                </span>
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="p-5 space-y-4 bg-gradient-to-b from-slate-900 to-slate-800">
            {/* Title - only show if no image or as small label */}
            {!imageUrl && (
              <h2 id="poster-popup-title" className="text-xl font-bold text-cyan-400">
                {config.title}
              </h2>
            )}
            
            {/* Message */}
            {config.message && (
              <p className="text-white/90 text-sm text-center">
                {config.message}
              </p>
            )}
            
            {/* Primary CTA - Prominent turquoise button */}
            {config.primaryCtaText && (
              <div className="pt-2">
                {isExternalLink(config.primaryCtaLink) ? (
                  <a
                    href={config.primaryCtaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handlePrimaryClick}
                    className="block w-full py-3.5 px-6 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-center rounded-xl transition-all transform hover:scale-[1.02] shadow-lg shadow-cyan-500/25"
                    data-testid="poster-popup-cta"
                  >
                    {config.primaryCtaText}
                  </a>
                ) : (
                  <Link
                    to={config.primaryCtaLink}
                    onClick={handlePrimaryClick}
                    className="block w-full py-3.5 px-6 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-center rounded-xl transition-all transform hover:scale-[1.02] shadow-lg shadow-cyan-500/25"
                    data-testid="poster-popup-cta"
                  >
                    {config.primaryCtaText}
                  </Link>
                )}
              </div>
            )}
            
            {/* Secondary WhatsApp link - subtle */}
            {config.secondaryCtaText && (
              <a
                href={config.secondaryCtaLink || globalPresets?.contact?.whatsapp_link || 'https://wa.me/26876817515'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {config.secondaryCtaText}
              </a>
            )}
          </div>
        </div>
      </div>
      
      {/* Lightbox for enlarged image */}
      {showLightbox && imageUrl && (
        <ImageLightbox 
          src={imageUrl} 
          alt={config.title || 'Holiday Program Flyer'} 
          onClose={() => setShowLightbox(false)} 
        />
      )}
    </>
  );
};

// Floating Banner Component
const FloatingBanner = ({ config, theme, onDismiss, globalPresets }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Delay visibility
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), config.delayMs || 4000);
    return () => clearTimeout(timer);
  }, [config.delayMs]);
  
  // Track impression
  useEffect(() => {
    if (isVisible) {
      trackPromoEvent('impression', config);
    }
  }, [isVisible, config]);
  
  const handlePrimaryClick = () => {
    trackPromoEvent('primary_click', config);
    if (config.frequencyMode === 'click') {
      storeDismiss(config.campaignId, 'click');
    }
  };
  
  const handleSecondaryClick = () => {
    trackPromoEvent('secondary_click', config);
  };
  
  const handleDismiss = () => {
    trackPromoEvent('dismiss', config);
    storeDismiss(config.campaignId, 'dismiss');
    onDismiss();
  };
  
  // Get WhatsApp link
  const whatsappLink = config.secondaryCtaLink || globalPresets?.contact?.whatsapp_link || 'https://wa.me/26876001234';
  const isExternalLink = (link) => link?.startsWith('http') || link?.startsWith('tel:') || link?.startsWith('mailto:');
  
  // Reduced motion check
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  
  if (!isVisible) return null;
  
  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-40 ${theme.bg} ${theme.text} shadow-2xl 
        transform transition-transform duration-300 ${prefersReducedMotion ? '' : 'animate-in slide-in-from-bottom'}
        pb-[env(safe-area-inset-bottom)]`}
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      role="dialog"
      aria-labelledby="promo-title"
      data-testid="promo-floating-banner"
    >
      {/* Close button */}
      <button
        onClick={handleDismiss}
        className={`absolute top-2 right-2 p-2 rounded-full ${theme.close} transition-colors`}
        aria-label="Dismiss promotion"
        data-testid="promo-dismiss-btn"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row items-center gap-4">
          {/* Icon/Image */}
          <div className="flex-shrink-0">
            {config.imageUrl ? (
              <img 
                src={config.imageUrl.startsWith('/') ? `${API_URL}${config.imageUrl}` : config.imageUrl} 
                alt="" 
                className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover"
                loading="lazy"
              />
            ) : (
              <div className={`p-3 rounded-xl bg-white/10`}>
                <Gift className={`w-8 h-8 ${theme.accent}`} />
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <h3 id="promo-title" className={`text-lg md:text-xl font-bold ${theme.accent}`}>
              {config.title}
            </h3>
            <p className="text-sm md:text-base opacity-90 mt-1">
              {config.message}
            </p>
          </div>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            {config.primaryCtaText && (
              isExternalLink(config.primaryCtaLink) ? (
                <a
                  href={config.primaryCtaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handlePrimaryClick}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold ${theme.button} transition-all`}
                  data-testid="promo-primary-cta"
                >
                  {config.primaryCtaText}
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={config.primaryCtaLink}
                  onClick={handlePrimaryClick}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold ${theme.button} transition-all`}
                  data-testid="promo-primary-cta"
                >
                  {config.primaryCtaText}
                </Link>
              )
            )}
            
            {config.secondaryCtaText && (
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSecondaryClick}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium ${theme.secondaryBtn} transition-all`}
                data-testid="promo-secondary-cta"
              >
                <MessageCircle className="w-4 h-4" />
                {config.secondaryCtaText}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Top Announcement Bar Component - positioned below the nav
const AnnouncementBar = ({ config, theme, onDismiss, globalPresets }) => {
  const handlePrimaryClick = () => {
    trackPromoEvent('topbar_primary_click', config);
    if (config.frequencyMode === 'click') {
      storeDismiss(config.campaignId, 'click');
    }
  };
  
  const handleDismiss = () => {
    trackPromoEvent('topbar_dismiss', config);
    storeDismiss(config.campaignId, 'dismiss');
    onDismiss();
  };
  
  const isExternalLink = (link) => link?.startsWith('http') || link?.startsWith('tel:') || link?.startsWith('mailto:');
  
  return (
    <div 
      className={`fixed top-16 left-0 right-0 z-40 ${theme.bg} ${theme.text} py-1.5 px-4 shadow-md`}
      role="banner"
      data-testid="promo-announcement-bar"
    >
      <div className="container mx-auto flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
        <Gift className={`w-4 h-4 ${theme.accent} flex-shrink-0`} />
        <span className="font-medium">{config.title}</span>
        <span className="hidden sm:inline opacity-75">—</span>
        <span className="hidden sm:inline opacity-90">{config.message}</span>
        
        {config.primaryCtaText && (
          isExternalLink(config.primaryCtaLink) ? (
            <a
              href={config.primaryCtaLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePrimaryClick}
              className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${theme.button} transition-all`}
            >
              {config.primaryCtaText}
            </a>
          ) : (
            <Link
              to={config.primaryCtaLink}
              onClick={handlePrimaryClick}
              className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${theme.button} transition-all`}
            >
              {config.primaryCtaText}
            </Link>
          )
        )}
        
        <button
          onClick={handleDismiss}
          className={`absolute right-2 p-1 rounded-full ${theme.close} transition-colors`}
          aria-label="Dismiss announcement"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Main PromoBanner Component
const PromoBanner = () => {
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [globalPresets, setGlobalPresets] = useState(null);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [topbarDismissed, setTopbarDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  // Fetch configs
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const [promoRes, presetsRes] = await Promise.all([
          fetch(`${API_URL}/api/landing/promo-config`),
          fetch(`${API_URL}/api/landing/global-presets-config`),
        ]);
        
        if (promoRes.ok) {
          const promoData = await promoRes.json();
          setConfig(promoData);
        }
        
        if (presetsRes.ok) {
          const presetsData = await presetsRes.json();
          setGlobalPresets(presetsData);
        }
      } catch (error) {
        console.error('Failed to fetch promo config:', error);
      }
    };
    
    fetchConfigs();
    setMounted(true);
  }, []);
  
  // Don't render until hydrated (avoid SSR mismatch)
  if (!mounted || !config) return null;
  
  // Check all conditions
  const currentPath = location.pathname;
  
  // Don't show on admin pages
  if (currentPath.startsWith('/admin')) return null;
  
  // Check enabled
  if (!config.enabled) return null;
  
  // Check date window
  if (!isWithinDateWindow(config.startAt, config.endAt)) return null;
  
  // Check route matching
  const matchesShowRoutes = config.showOnRoutes?.length === 0 || 
    config.showOnRoutes?.some(route => currentPath === route || currentPath.startsWith(route + '/'));
  const matchesHideRoutes = config.hideOnRoutes?.some(route => currentPath === route || currentPath.startsWith(route + '/'));
  
  if (!matchesShowRoutes || matchesHideRoutes) return null;
  
  // Check device targeting
  const deviceType = getDeviceType();
  if (!config.showOn?.includes(deviceType)) return null;
  
  // Check frequency for localStorage-based persistence
  const isFrequencyDismissed = isDismissedRecently(config.campaignId, config.frequencyDays, config.frequencyMode);
  
  const theme = themes[config.theme] || themes.default;
  
  // Separate handlers for popup and topbar
  const handlePopupDismiss = () => setPopupDismissed(true);
  const handleTopbarDismiss = () => setTopbarDismissed(true);
  
  // Determine what to show
  const showPopup = (config.type === 'popup' || config.type === 'popup_topbar') && !popupDismissed && !isFrequencyDismissed;
  const showTopbar = (config.type === 'topbar' || config.type === 'both' || config.type === 'popup_topbar') && !topbarDismissed && !isFrequencyDismissed;
  const showFloating = (config.type === 'floating' || config.type === 'both') && !popupDismissed && !isFrequencyDismissed;
  
  return (
    <>
      {/* Top Announcement Bar - independently dismissible */}
      {showTopbar && (
        <AnnouncementBar 
          config={config} 
          theme={theme} 
          onDismiss={handleTopbarDismiss}
          globalPresets={globalPresets}
        />
      )}
      
      {/* Poster Popup - New image-first popup */}
      {showPopup && (
        <PosterPopup 
          config={config} 
          theme={theme} 
          onDismiss={handlePopupDismiss}
          globalPresets={globalPresets}
        />
      )}
      
      {/* Legacy Floating Banner - kept for backward compatibility */}
      {showFloating && (
        <FloatingBanner 
          config={config} 
          theme={theme} 
          onDismiss={handlePopupDismiss}
          globalPresets={globalPresets}
        />
      )}
    </>
  );
};

export default PromoBanner;
